import { Box, ChartSection, ChartHeader, Text } from '@instamenu/ui-system-next';
import { formatCurrency } from '@instamenu/formatters';
import { formatTimespan, formatTimeRange } from '@instamenu/formatters';
import { useSalesSummaryContext } from '../../SalesSummaryContext';
import { useMediaQuery } from 'react-responsive';
import { useLocale } from '@instamenu/context-i18n';
import {
  expandedDescription,
  styledColumns,
  styledSection,
  styledRow,
  collapsedDescription,
} from './Overview.css';
import { WithDescription } from './WithDescription';
import { palette } from '@instamenu/ui-vanilla-extract';
import { DateTime } from 'luxon';
import { StatsQuery } from '../../queries.generated';
import { CircleArrowUp, Info } from 'lucide-react';
import {
  formatSignedCurrency,
  hasCashRoundingAdjustment,
  totalCollectedWithCashRounding,
} from '../../../cash-rounding';
import { thirdPartyPayoutSummary } from './third-party-payouts';

const now = DateTime.now();

type StatsDatatype = NonNullable<StatsQuery['stats']>;

export default function Overview() {
  const { statsQuery, timespan, times } = useSalesSummaryContext();
  const [{ data, error, fetching }] = statsQuery;
  const { i18n } = useLocale();

  const csvOptions = () => {
    const stats = data?.stats;
    const thirdPartyPayout = thirdPartyPayoutSummary(stats);
    return {
      data: stats
        ? [
            {},
            { label: i18n.t('salesSummary.overview.sales') },
            {
              label: i18n.t('salesSummary.overview.grossSales'),
              value: formatCurrency(stats.grossSales),
            },
            {
              label: i18n.t('salesSummary.overview.refunds'),
              value: formatCurrency(stats.netSalesRefunds),
            },
            {
              label: i18n.t('salesSummary.overview.discounts'),
              value: formatCurrency(stats.totalDiscounts),
            },
            ...(stats.loyaltyComps
              ? [
                  {
                    label: i18n.t('salesSummary.overview.loyalty'),
                    value: formatCurrency(stats.loyaltyComps),
                  },
                ]
              : []),
            {
              label: i18n.t('salesSummary.overview.netSales'),
              value: formatCurrency(stats.netSales),
            },
            { label: i18n.t('salesSummary.overview.salesTax'), value: formatCurrency(stats.tax) },
            { label: i18n.t('salesSummary.overview.cardTips'), value: formatCurrency(stats.tips) },
            {
              label: i18n.t('salesSummary.overview.salesTotal'),
              value: formatCurrency(stats.netTotal),
            },
            ...(hasCashRoundingAdjustment(stats.cashRoundingAdjustment)
              ? [
                  {
                    label: i18n.t('salesSummary.overview.cashRounding'),
                    value: formatSignedCurrency(stats.cashRoundingAdjustment),
                  },
                ]
              : []),
            {
              label: i18n.t('salesSummary.overview.invoiceNetTotal'),
              value: formatCurrency(stats.invoiceNetTotal),
            },
            {},
            { label: i18n.t('salesSummary.overview.payout') },
            {
              label: i18n.t('salesSummary.overview.totalCollected'),
              value: formatCurrency(totalCollectedWithCashRounding(stats)),
            },
            {
              label: i18n.t('salesSummary.overview.usingCash'),
              value: formatCurrency(stats.cashPayments),
            },
            ...(thirdPartyPayout.hasActiveStreamConnection
              ? [
                  {
                    label: i18n.t('salesSummary.overview.usingThirdParty'),
                    value: formatCurrency(thirdPartyPayout.externalOrderTotal),
                  },
                ]
              : []),
            {
              label: i18n.t('salesSummary.overview.usingGiftCards'),
              value: formatCurrency(stats.giftCardPayments),
            },
            {
              label: i18n.t('salesSummary.overview.salesTaxWithheld'),
              value: formatCurrency(stats.salesTaxWithheld),
            },
            {
              label: i18n.t('salesSummary.overview.merchantFees'),
              value: formatCurrency(stats.merchantFee),
            },
            {
              label: i18n.t('salesSummary.overview.totalPayout'),
              value: formatCurrency(stats.expectedPayout),
            },
            {},
            { label: i18n.t('salesSummary.overview.performance') },
            { label: i18n.t('salesSummary.overview.transactions'), value: stats.numOrders },
            {
              label: i18n.t('salesSummary.overview.avgSale'),
              value: formatCurrency(stats.averageSale),
            },
            {
              label: i18n.t('salesSummary.overview.avgCardTip'),
              value: `${formatCurrency(stats.averageTip)} (${stats.averageTipPercent}%)`,
            },
            {},
            { label: i18n.t('salesSummary.overview.instamenu') },
            {
              label: i18n.t('salesSummary.overview.processingFees'),
              value: formatCurrency(stats.processingFee),
            },
            {
              label: i18n.t('salesSummary.overview.deliveryFees'),
              value: formatCurrency(stats.deliveryFee),
            },
          ]
        : undefined,
      columns: [
        { header: 'Label', accessorKey: 'label' },
        { header: 'Value', accessorKey: 'value' },
      ],
      filename: `overview.csv`,
    };
  };

  return (
    <ChartSection
      error={error}
      loading={fetching}
      empty={!data?.stats}
      divide
      Header={
        <ChartHeader
          title={i18n.t('salesSummary.overview.overview')}
          subtitle={[
            formatTimespan(timespan, i18n) +
              (now.hasSame(timespan.start, 'day') &&
              now.hasSame(timespan.end, 'day') &&
              !times.startTime &&
              !times.endTime
                ? i18n.t('salesSummary.overview.upTo', {
                    time: now.toFormat('hh:mm a'),
                  })
                : ''),
            formatTimeRange(times),
          ]
            .filter((x) => x)
            .join('; ')}
          csvOptions={csvOptions()}
        />
      }
    >
      {data?.stats ? <ChartData data={data?.stats} /> : null}
    </ChartSection>
  );
}
const StyledSection = ({
  children,
  css,
}: {
  children: React.ReactNode;
  css?: React.CSSProperties;
}) => {
  return (
    <div style={css} className={styledSection}>
      {children}
    </div>
  );
};
const StyledDescription = ({
  children,
  expanded = false,
}: {
  children: React.ReactNode;
  expanded?: boolean;
}) => {
  return <div className={expanded ? expandedDescription : collapsedDescription}>{children}</div>;
};

const StyledRow = ({ children, css }: { children: React.ReactNode; css?: React.CSSProperties }) => {
  return (
    <div className={styledRow} style={css}>
      {children}
    </div>
  );
};

const StyledColumns = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className={styledColumns} style={{ display: 'flex', columnGap: '48px', border: 'none' }}>
      {children}
    </div>
  );
};

function Expand({ expanded, color }: { expanded: boolean; color?: string }) {
  const Icon = expanded ? CircleArrowUp : Info;
  return <Icon color={color ?? palette.gray1100} />;
}

function Payout({ data }: { data: StatsDatatype }) {
  const { i18n } = useLocale();
  const thirdPartyPayout = thirdPartyPayoutSummary(data);
  const externalOrderTotalsBySource = data.externalOrderTotalsBySource ?? [];
  return (
    <StyledSection>
      <Text variant="h2">{i18n.t('salesSummary.overview.payout')}</Text>
      <WithDescription
        row={(props) => (
          <StyledRow css={{ border: 'none' }}>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.totalCollected')}</Text>
            <Expand expanded={props.expanded} />
            <Text variant="subtitle1" weight="bold">
              {formatCurrency(totalCollectedWithCashRounding(data))}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.totalCollectedDescription')}</p>
            <table style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <td>{i18n.t('salesSummary.overview.salesTotal')}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.netTotal)}</td>
                </tr>
                <tr>
                  <td>{i18n.t('salesSummary.overview.invoiceNetTotal')}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.invoiceNetTotal)}</td>
                </tr>
                <tr>
                  <td>{i18n.t('salesSummary.overview.giftCardsSold')}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.giftCardSales)}</td>
                </tr>
                {hasCashRoundingAdjustment(data.cashRoundingAdjustment) && (
                  <tr>
                    <td>{i18n.t('salesSummary.overview.cashRounding')}</td>
                    <td style={{ textAlign: 'right' }}>
                      {formatSignedCurrency(data.cashRoundingAdjustment)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </StyledDescription>
        )}
      />
      <WithDescription
        row={(props) => (
          <StyledRow css={{ color: palette.gray400 }}>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.usingCash')}</Text>
            <Expand expanded={props.expanded} color={palette.gray400} />
            <Text variant="subtitle1" weight="medium">
              {formatCurrency(data.cashPayments)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.usingCashDescription')}</p>
          </StyledDescription>
        )}
      />
      {thirdPartyPayout.hasActiveStreamConnection && (
        <WithDescription
          row={(props) => (
            <StyledRow css={{ color: palette.gray400 }}>
              <Text variant="subtitle2">{i18n.t('salesSummary.overview.usingThirdParty')}</Text>
              <Expand expanded={props.expanded} color={palette.gray400} />
              <Text variant="subtitle1" weight="medium">
                {formatCurrency(thirdPartyPayout.externalOrderTotal)}
              </Text>
            </StyledRow>
          )}
          description={(props) => (
            <StyledDescription {...props}>
              <p>{i18n.t('salesSummary.overview.usingThirdPartyDescription')}</p>
              <table style={{ width: '100%' }}>
                <tbody>
                  {externalOrderTotalsBySource.map((source) => (
                    <tr key={source.sourceLabel}>
                      <td>{source.sourceLabel}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(source.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StyledDescription>
          )}
        />
      )}
      <WithDescription
        row={(props) => (
          <StyledRow css={{ color: palette.gray400 }}>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.usingGiftCards')}</Text>
            <Expand expanded={props.expanded} color={palette.gray400} />
            <Text variant="subtitle1" weight="medium">
              {formatCurrency(data.giftCardPayments)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.usingGiftCardsDescription')}</p>
          </StyledDescription>
        )}
      />
      <WithDescription
        row={(props) => (
          <StyledRow css={{ color: palette.gray400 }}>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.salesTaxWithheld')}</Text>
            <Expand expanded={props.expanded} color={palette.gray400} />
            <Text variant="subtitle1" weight="medium">
              {formatCurrency(data.salesTaxWithheld)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.salesTaxWithheldDescription')}</p>
          </StyledDescription>
        )}
      />
      <WithDescription
        row={(props) => (
          <StyledRow css={{ color: palette.gray400 }}>
            <Box>
              <Box css={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text variant="subtitle2">{i18n.t('salesSummary.overview.merchantFees')}</Text>
                <Expand expanded={props.expanded} color={palette.gray400} />
              </Box>
            </Box>
            <Text variant="subtitle1" weight="medium">
              {formatCurrency(data.merchantFee)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.merchantFeesDescription')}</p>
          </StyledDescription>
        )}
      />
      <WithDescription
        row={(props) => (
          <StyledRow>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.totalPayout')}</Text>
            <Expand expanded={props.expanded} />
            <Text variant="subtitle1" weight="bold">
              {formatCurrency(data.expectedPayout)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.totalPayoutDescription')}</p>
          </StyledDescription>
        )}
      />
    </StyledSection>
  );
}

function Sales({ data }: { data: StatsDatatype }) {
  const { i18n } = useLocale();
  return (
    <StyledSection css={{ order: 1, width: '100%' }}>
      <Text variant="h2">{i18n.t('salesSummary.overview.sales')}</Text>
      <WithDescription
        row={(props) => (
          <StyledRow css={{ border: 'none' }}>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.grossSales')}</Text>
            <Expand expanded={props.expanded} />
            <Text variant="subtitle1" weight="bold">
              {formatCurrency(data.grossSales)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.grossSalesDescription')}</p>
            <p>{i18n.t('salesSummary.overview.grossSalesCalc')}</p>
            <table style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <td>{i18n.t('salesSummary.overview.items')}</td>
                  <td style={{ textAlign: 'right' }}>
                    {formatCurrency(data.grossSales - data.surchargeAmount)}
                  </td>
                </tr>
                <tr>
                  <td>{i18n.t('salesSummary.overview.surcharges')}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.surchargeAmount)}</td>
                </tr>
              </tbody>
            </table>
          </StyledDescription>
        )}
      />
      <WithDescription
        row={(props) => (
          <StyledRow css={{ color: palette.gray400 }}>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.refunds')}</Text>
            <Expand expanded={props.expanded} color={palette.gray400} />
            <Text variant="subtitle1" weight="medium">
              {formatCurrency(data.netSalesRefunds)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.refundsDescription')}</p>
          </StyledDescription>
        )}
      />
      <WithDescription
        row={(props) => (
          <StyledRow css={{ color: palette.gray400 }}>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.discounts')}</Text>
            <Expand expanded={props.expanded} color={palette.gray400} />
            <Text variant="subtitle1" weight="medium">
              {formatCurrency(data.totalDiscounts)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.discountsDescription')}</p>
          </StyledDescription>
        )}
      />
      {!!data.loyaltyComps && (
        <WithDescription
          row={(props) => (
            <StyledRow css={{ color: palette.gray400 }}>
              <Text variant="subtitle2">{i18n.t('salesSummary.overview.loyalty')}</Text>
              <Expand expanded={props.expanded} color={palette.gray400} />
              <Text variant="subtitle1" weight="medium">
                {formatCurrency(data.loyaltyComps)}
              </Text>
            </StyledRow>
          )}
          description={(props) => (
            <StyledDescription {...props}>
              <p>{i18n.t('salesSummary.overview.loyaltyDescription')}</p>
            </StyledDescription>
          )}
        />
      )}
      <WithDescription
        row={(props) => (
          <StyledRow>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.netSales')}</Text>
            <Expand expanded={props.expanded} />
            <Text variant="subtitle1" weight="bold">
              {formatCurrency(data.netSales)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.netSalesDescription')}</p>
            <p>{i18n.t('salesSummary.overview.netSalesCalc')}</p>
          </StyledDescription>
        )}
      />
      <WithDescription
        row={(props) => (
          <StyledRow css={{ color: palette.gray400 }}>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.salesTax')}</Text>
            <Expand expanded={props.expanded} color={palette.gray400} />
            <Text variant="subtitle1" weight="medium">
              {formatCurrency(data.tax)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.salesTaxDescription')}</p>
          </StyledDescription>
        )}
      />
      <WithDescription
        row={(props) => (
          <StyledRow css={{ color: palette.gray400 }}>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.cardTips')}</Text>
            <Expand expanded={props.expanded} color={palette.gray400} />
            <Text variant="subtitle1" weight="medium">
              {formatCurrency(data.tips)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.cardTipsDescription')}</p>
          </StyledDescription>
        )}
      />
      <WithDescription
        row={(props) => (
          <StyledRow>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.salesTotal')}</Text>
            <Expand expanded={props.expanded} />
            <Text variant="subtitle1" weight="bold">
              {formatCurrency(data.netTotal)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.salesTotalDescription')}</p>
          </StyledDescription>
        )}
      />
      {hasCashRoundingAdjustment(data.cashRoundingAdjustment) && (
        <WithDescription
          row={(props) => (
            <StyledRow css={{ color: palette.gray400, marginTop: 24 }}>
              <Text variant="subtitle2">{i18n.t('salesSummary.overview.cashRounding')}</Text>
              <Expand expanded={props.expanded} color={palette.gray400} />
              <Text variant="subtitle1" weight="medium">
                {formatSignedCurrency(data.cashRoundingAdjustment)}
              </Text>
            </StyledRow>
          )}
          description={(props) => (
            <StyledDescription {...props}>
              <p>{i18n.t('salesSummary.overview.cashRoundingDescription')}</p>
            </StyledDescription>
          )}
        />
      )}
      <WithDescription
        row={(props) => (
          <StyledRow css={{ marginTop: 24 }}>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.invoiceNetTotal')}</Text>
            <Expand expanded={props.expanded} />
            <Text variant="subtitle1" weight="bold">
              {formatCurrency(data.invoiceNetTotal)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.invoiceNetTotalDescription')}</p>
          </StyledDescription>
        )}
      />
    </StyledSection>
  );
}

function Performance({ data }: { data: StatsDatatype }) {
  const { i18n } = useLocale();
  return (
    <StyledSection css={{ order: 1 }}>
      <Text variant="h2">{i18n.t('salesSummary.overview.performance')}</Text>
      <WithDescription
        row={(props) => (
          <StyledRow css={{ border: 'none' }}>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.transactions')}</Text>
            <Expand expanded={props.expanded} />
            <Text variant="subtitle1" weight="bold">
              {data.numOrders}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.transactionsDescription')}</p>
          </StyledDescription>
        )}
      />
      <WithDescription
        row={(props) => (
          <StyledRow>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.avgSale')}</Text>
            <Expand expanded={props.expanded} />
            <Text variant="subtitle1" weight="bold">
              {formatCurrency(data.averageSale)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.avgSaleDescription')}</p>
            <p>{i18n.t('salesSummary.overview.avgSaleCalc')}</p>
            <p>{i18n.t('salesSummary.overview.avgSaleCalcTwo')}</p>
          </StyledDescription>
        )}
      />
      <WithDescription
        row={(props) => (
          <StyledRow>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.avgCardTip')}</Text>
            <Expand expanded={props.expanded} />
            <Text variant="subtitle1" weight="bold">
              {formatCurrency(data.averageTip)} ({data.averageTipPercent}%)
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.avgCardTipDescription')}</p>
          </StyledDescription>
        )}
      />
    </StyledSection>
  );
}

function Instamenu({ data }: { data: StatsDatatype }) {
  const { i18n } = useLocale();
  return (
    <StyledSection css={{ order: 1 }}>
      <Text variant="h2">{i18n.t('salesSummary.overview.instamenu')}</Text>
      <WithDescription
        row={(props) => (
          <StyledRow>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.processingFees')}</Text>
            <Expand expanded={props.expanded} color={palette.gray400} />
            <Text variant="subtitle1" weight="medium">
              {formatCurrency(data.processingFee)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.processingFeesDescription')}</p>
          </StyledDescription>
        )}
      />
      <WithDescription
        row={(props) => (
          <StyledRow>
            <Text variant="subtitle2">{i18n.t('salesSummary.overview.deliveryFees')}</Text>
            <Expand expanded={props.expanded} color={palette.gray400} />
            <Text variant="subtitle1" weight="medium">
              {formatCurrency(data.deliveryFee)}
            </Text>
          </StyledRow>
        )}
        description={(props) => (
          <StyledDescription {...props}>
            <p>{i18n.t('salesSummary.overview.deliveryFeesDescription')}</p>
          </StyledDescription>
        )}
      />
    </StyledSection>
  );
}

function ChartData({ data }: { data: StatsDatatype }) {
  const isDesktop = useMediaQuery({ query: `(min-width: 768px)` });
  return isDesktop ? (
    <StyledColumns>
      <div style={{ flex: '51%' }}>
        <Sales data={data} />
        <Instamenu data={data} />
      </div>
      <div style={{ flex: '51%' }}>
        <Payout data={data} />
        <Performance data={data} />
      </div>
    </StyledColumns>
  ) : (
    <>
      <Payout data={data} />
      <Sales data={data} />
      <Performance data={data} />
      <Instamenu data={data} />
    </>
  );
}
